import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Biotech as TrialIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as ExcludeIcon,
  OpenInNew as ExternalIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getClinicalTrials } from '../../services/tumorBoardService';
import { CANCER_TYPES, CLINICAL_TRIAL_PHASES } from '../../types/enums';
import { PageHeader, ContentCard, HeaderButton } from './oncologyStyles';

const ClinicalTrialSearch = ({ patientDiagnosis, onEnroll, embedded = false }) => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trials, setTrials] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState({
    cancerType: patientDiagnosis?.cancerType || '',
    phase: '',
    status: 'Recruiting'
  });

  const searchTrials = useCallback(async () => {
    if (!defaultFacility?.id) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getClinicalTrials(defaultFacility.id, {
        cancerType: searchCriteria.cancerType,
        trialStatus: searchCriteria.status
      });
      setTrials(result.data || []);
    } catch (err) {
      console.error('Error searching clinical trials:', err);
      setError('Failed to search clinical trials');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, searchCriteria]);

  useEffect(() => {
    if (defaultFacility?.id) {
      searchTrials();
    }
  }, [searchTrials, defaultFacility?.id]);

  const handleSearch = () => {
    searchTrials();
  };

  const handleEnroll = (trial) => {
    if (onEnroll) {
      onEnroll(trial);
    }
  };

  const trialStatuses = ['Recruiting', 'Active', 'Completed', 'Suspended'];

  const searchFilters = (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Cancer Type</InputLabel>
        <Select
          value={searchCriteria.cancerType}
          label="Cancer Type"
          onChange={(e) => setSearchCriteria(prev => ({ ...prev, cancerType: e.target.value }))}
        >
          <MenuItem value="">All Types</MenuItem>
          {CANCER_TYPES.map(type => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Trial Phase</InputLabel>
        <Select
          value={searchCriteria.phase}
          label="Trial Phase"
          onChange={(e) => setSearchCriteria(prev => ({ ...prev, phase: e.target.value }))}
        >
          <MenuItem value="">All Phases</MenuItem>
          {CLINICAL_TRIAL_PHASES.map(phase => (
            <MenuItem key={phase} value={phase}>{phase}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={searchCriteria.status}
          label="Status"
          onChange={(e) => setSearchCriteria(prev => ({ ...prev, status: e.target.value }))}
        >
          {trialStatuses.map(status => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        size="small"
        onClick={handleSearch}
        startIcon={<SearchIcon />}
        disabled={loading}
        sx={{ height: 40 }}
      >
        Search
      </Button>
    </Box>
  );

  const resultsContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : trials.length === 0 ? (
        <Alert severity="info" sx={{ py: 1 }}>
          No clinical trials found matching your criteria. Try adjusting your search filters.
        </Alert>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {trials.length} trial{trials.length !== 1 ? 's' : ''} found
          </Typography>
          {trials.map((trial) => (
            <Accordion 
              key={trial.id} 
              variant="outlined" 
              sx={{ 
                mb: 0.5, 
                borderRadius: '6px !important', 
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': { minHeight: 48 },
                '& .MuiAccordionSummary-content': { my: 1 }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {trial.shortTitle || trial.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {trial.nctNumber} • {trial.phase}
                    </Typography>
                  </Box>
                  <Chip
                    label={trial.trialStatus}
                    color={trial.trialStatus === 'Recruiting' ? 'success' : 'default'}
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{trial.title}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Sponsor</Typography>
                    <Typography variant="body2" fontSize="0.8rem">{trial.sponsor || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">PI</Typography>
                    <Typography variant="body2" fontSize="0.8rem">{trial.principalInvestigatorName || trial.principalInvestigator || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Enrollment</Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {trial.currentEnrollment || 0} / {trial.targetEnrollment || '?'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Cancer Types</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {trial.cancerTypes?.slice(0, 2).map(type => (
                        <Chip key={type} label={type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      ))}
                      {trial.cancerTypes?.length > 2 && (
                        <Chip label={`+${trial.cancerTypes.length - 2}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </Grid>

                  {trial.eligibilityCriteria && (trial.eligibilityCriteria.inclusion?.length > 0 || trial.eligibilityCriteria.exclusion?.length > 0) && (
                    <Grid item xs={12}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mt: 0.5, display: 'block' }}>Eligibility</Typography>
                      <Grid container spacing={1}>
                        {trial.eligibilityCriteria.inclusion?.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <List dense disablePadding>
                              {trial.eligibilityCriteria.inclusion.slice(0, 3).map((criterion, i) => (
                                <ListItem key={i} sx={{ py: 0, px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 20 }}>
                                    <CheckIcon sx={{ fontSize: 14 }} color="success" />
                                  </ListItemIcon>
                                  <ListItemText primary={criterion} primaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        )}
                        {trial.eligibilityCriteria.exclusion?.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <List dense disablePadding>
                              {trial.eligibilityCriteria.exclusion.slice(0, 3).map((criterion, i) => (
                                <ListItem key={i} sx={{ py: 0, px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 20 }}>
                                    <ExcludeIcon sx={{ fontSize: 14 }} color="error" />
                                  </ListItemIcon>
                                  <ListItemText primary={criterion} primaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
                      {trial.nctNumber && (
                        <Button
                          size="small"
                          startIcon={<ExternalIcon sx={{ fontSize: 14 }} />}
                          href={`https://clinicaltrials.gov/study/${trial.nctNumber}`}
                          target="_blank"
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          ClinicalTrials.gov
                        </Button>
                      )}
                      {onEnroll && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleEnroll(trial)}
                          disabled={trial.trialStatus !== 'Recruiting'}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          Enroll Patient
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </>
  );

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <TrialIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Clinical Trial Search
            </Typography>
          </Box>
          {searchFilters}
          {resultsContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page
  return (
    <Box>
      {/* Header with Back Navigation */}
      <PageHeader
        title="Clinical Trials"
        subtitle="Search and manage clinical trial enrollments"
        icon={<TrialIcon />}
        backTo="/oncology"
        actions={
          <HeaderButton
            startIcon={<AddIcon />}
            onClick={() => navigate('/oncology/clinical-trials/new')}
          >
            New Trial
          </HeaderButton>
        }
      />

      <ContentCard>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {searchFilters}
          {resultsContent}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default ClinicalTrialSearch;
